import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

export interface ShortlistModel {
  id: string;
  name: string;
  image: string;
  agency: string;
  isRealModel?: boolean; // true if it's a real DB model, false for sample models
}

interface ShortlistContextType {
  shortlistModels: ShortlistModel[];
  isPanelOpen: boolean;
  setIsPanelOpen: (open: boolean) => void;
  addToShortlist: (model: ShortlistModel) => void;
  removeFromShortlist: (modelId: string) => void;
  isInShortlist: (modelId: string) => boolean;
  clearShortlist: () => void;
  getRealModelIds: () => string[]; // Returns only real model IDs for saving
  hasSampleModels: () => boolean; // Returns true if any sample models are in shortlist
}

const STORAGE_KEY = "focus-shortlist";

// Context for sharing shortlist state across the app
const ShortlistContext = createContext<ShortlistContextType | null>(null);

export const ShortlistProvider = ({ children }: { children: ReactNode }) => {
  const [shortlistModels, setShortlistModels] = useState<ShortlistModel[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Sync to localStorage whenever shortlist changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shortlistModels));
  }, [shortlistModels]);

  const addToShortlist = useCallback((model: ShortlistModel) => {
    setShortlistModels((prev) => {
      if (prev.some((m) => m.id === model.id)) return prev;
      return [...prev, model];
    });
    setIsPanelOpen(true);
  }, []);

  const removeFromShortlist = useCallback((modelId: string) => {
    setShortlistModels((prev) => prev.filter((m) => m.id !== modelId));
  }, []);

  const isInShortlist = useCallback(
    (modelId: string) => shortlistModels.some((m) => m.id === modelId),
    [shortlistModels]
  );

  const clearShortlist = useCallback(() => {
    setShortlistModels([]);
  }, []);

  const getRealModelIds = useCallback(() => {
    return shortlistModels
      .filter((m) => m.isRealModel === true)
      .map((m) => m.id);
  }, [shortlistModels]);

  const hasSampleModels = useCallback(() => {
    return shortlistModels.some((m) => m.isRealModel !== true);
  }, [shortlistModels]);

  return (
    <ShortlistContext.Provider
      value={{
        shortlistModels,
        isPanelOpen,
        setIsPanelOpen,
        addToShortlist,
        removeFromShortlist,
        isInShortlist,
        clearShortlist,
        getRealModelIds,
        hasSampleModels,
      }}
    >
      {children}
    </ShortlistContext.Provider>
  );
};

export const useShortlist = () => {
  const context = useContext(ShortlistContext);
  if (!context) {
    throw new Error("useShortlist must be used within a ShortlistProvider");
  }
  return context;
};
